import { useEffect, useEffectEvent, useMemo, useRef, useState, type ReactNode } from 'react';
import { ArrowBackOutlined, ContentPasteOutlined, DownloadOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DialogSurface, FlowSteps, FormTextField, StatusBadge, ValueBadge, designTokens } from '../../../design-system';
import type { BulkApplyResult, BulkApplyRowResult, BulkPreviewColumn, BulkReferenceMatch, BulkValidatedRow, BulkWorkflow } from '../model/bulkOperations';
import { parseTSV, type ParsedTSV } from '../model/tsv';
import { BulkReferenceReview } from './BulkReferenceReview';
import { BulkPreApplyValidationError } from '../model/applyValidation';
import { preserveBulkReferenceSelections, resolveBulkReferences, validateBulkValues, type BulkReferenceField, type BulkReferenceOption, type BulkReferencePort } from '../model/references';

function previewValue(column: BulkPreviewColumn, row: BulkValidatedRow) {
  const match = column.referenceKey ? row.referenceMatches?.[column.referenceKey] : undefined;
  if (!match) {
    const labels = Object.fromEntries(Object.entries(row.referenceMatches ?? {}).flatMap(([key, reference]) => reference.status === 'MATCHED' && reference.matched ? [[key, reference.matched.descriptor]] : []));
    return column.value({ ...row.values, ...labels }) || '—';
  }

  const labels: Record<BulkReferenceMatch['status'], string> = {
    MATCHED: '연결됨',
    NOT_FOUND: '일치 항목 없음',
    AMBIGUOUS: '중복 일치',
    MISSING: '입력 누락',
  };
  return <Stack spacing={0.5} sx={{ alignItems: 'flex-start', overflowWrap: 'anywhere' }}>
    {match.matched?.id !== match.input && <Typography variant="body2">입력: {match.input || '미입력'}</Typography>}
    <StatusBadge tone={match.status === 'MATCHED' ? 'success' : 'warning'} label={labels[match.status]} />
    {match.status === 'MATCHED' && match.matched ? <>
      <Typography variant="body2">{match.matched.descriptor}</Typography>
    </> : null}
    {match.status === 'AMBIGUOUS' && match.candidateCount !== undefined
      ? <Typography variant="caption" color="text.secondary">후보 {match.candidateCount.toLocaleString()}개</Typography>
      : null}
  </Stack>;
}

type BulkPasteDialogProps = {
  open: boolean;
  workflows: BulkWorkflow[];
  initialOperationType?: string;
  columnMapping?: boolean;
  help?: ReactNode;
  introduction?: ReactNode;
  inputControls?: (disabled: boolean) => ReactNode;
  validationContext?: string;
  validationDisabled?: boolean;
  prepareRows?: (rows: Record<string, string>[]) => BulkValidatedRow[];
  referencePort?: BulkReferencePort;
  onClose: () => void;
  onApply: (operationType: string, rows: BulkValidatedRow[], idempotencyKey: string) => Promise<void | BulkApplyResult>;
};

export function BulkPasteDialog({ open, workflows, initialOperationType, onClose, onApply, columnMapping = false, help, introduction, inputControls, validationContext, validationDisabled = false, prepareRows, referencePort }: BulkPasteDialogProps) {
  const [operationType, setOperationType] = useState(initialOperationType ?? workflows[0].operationType);
  const [step, setStep] = useState<'paste' | 'map' | 'review' | 'result'>('paste');
  const [input, setInput] = useState('');
  const [parsedInput, setParsedInput] = useState<ParsedTSV>();
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [validatedRows, setValidatedRows] = useState<BulkValidatedRow[]>([]);
  const [applyResults, setApplyResults] = useState<BulkApplyRowResult[]>([]);
  const [error, setError] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyStarted, setApplyStarted] = useState(false);
  const [resolving, setResolving] = useState(false);
  const resolutionSequence = useRef(0);
  const sourceRows = useRef<Record<string, string>[]>([]);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const applyingRef = useRef(false);
  const workflow = workflows.find((item) => item.operationType === operationType) ?? workflows[0];
  const workflowKey = workflows.map((item) => item.operationType).join(',');

  const resetDialog = useEffectEvent(() => {
    referencePort?.reset?.();
    resolutionSequence.current += 1;
    setResolving(false);
    setOperationType(initialOperationType && workflows.some((item) => item.operationType === initialOperationType) ? initialOperationType : workflows[0].operationType);
    setStep('paste');
    setInput('');
    setMapping({});
    setParsedInput(undefined);
    sourceRows.current = [];
    setValidatedRows([]);
    setApplyResults([]);
    setError('');
    setApplying(false);
    setApplyStarted(false);
    applyingRef.current = false;
    setIdempotencyKey(crypto.randomUUID());
  });
  useEffect(() => {
    if (!open) return;
    resetDialog();
    return () => { resolutionSequence.current += 1; };
  }, [initialOperationType, open, workflowKey]);

  const invalidateContext = useEffectEvent(() => {
    if (applyStarted) return;
    resolutionSequence.current += 1;
    referencePort?.reset?.();
    sourceRows.current = [];
    setValidatedRows([]);
    setApplyResults([]);
    setResolving(false);
    setError('');
    setStep('paste');
    setIdempotencyKey(crypto.randomUUID());
  });
  useEffect(() => { invalidateContext(); }, [validationContext]);

  const invalidRows = useMemo(() => validatedRows.filter((row) => row.errors.length > 0), [validatedRows]);
  const resultByRow = useMemo(() => new Map(applyResults.map((row) => [row.rowNumber, row])), [applyResults]);
  const failedResults = useMemo(() => applyResults.filter((row) => row.status === 'FAILED'), [applyResults]);
  const succeededResults = useMemo(() => applyResults.filter((row) => row.status === 'SUCCEEDED'), [applyResults]);
  const previewRows = useMemo(() => [...validatedRows].sort((left, right) => {
    if (step === 'result') {
      return Number(resultByRow.get(right.rowNumber)?.status === 'FAILED') - Number(resultByRow.get(left.rowNumber)?.status === 'FAILED');
    }
    return Number(Boolean(right.errors.length)) - Number(Boolean(left.errors.length));
  }).slice(0, 200), [resultByRow, step, validatedRows]);
  const example = [workflow.columns.map((column) => columnMapping || referencePort ? column.label : column.key).join('\t'), workflow.columns.map((column) => column.example).join('\t')].join('\n');

  const changeWorkflow = (nextOperationType: string) => {
    if (resolving || applying || applyStarted) return;
    resolutionSequence.current += 1;
    setOperationType(nextOperationType);
    setInput('');
    sourceRows.current = [];
    setValidatedRows([]);
    setApplyResults([]);
    setError('');
  };

  const resolveRows = async (values: Record<string, string>[], originalValues = values) => {
    const sequence = ++resolutionSequence.current;
    setResolving(true);
    try {
      const prepared = prepareRows?.(values) ?? values.map((row, index) => ({ values: row, rowNumber: index + 2, errors: [] }));
      const checked = validateBulkValues(workflow, prepared);
      const result = referencePort ? await resolveBulkReferences(workflow, checked, referencePort) : checked;
      if (sequence === resolutionSequence.current) {
        sourceRows.current = originalValues;
        setValidatedRows(result.map((row, index) => ({ ...row, referenceMatches: Object.fromEntries(Object.entries(row.referenceMatches ?? {}).map(([key, match]) => [key, { ...match, input: match.status === 'MATCHED' ? originalValues[index]?.[key] ?? match.input : match.input }])) })));
      }
      return sequence === resolutionSequence.current;
    } catch (failure) {
      if (sequence === resolutionSequence.current) {
        const message = failure instanceof Error ? failure.message : '대상을 확인하지 못했습니다.';
        setError(message);
        setValidatedRows((current) => current.map((row) => ({ ...row, errors: [...row.errors, message] })));
      }
      return false;
    } finally { if (sequence === resolutionSequence.current) setResolving(false); }
  };

  const changeReference = (rowNumbers: number[], field: BulkReferenceField, option: BulkReferenceOption | null) => {
    if (applying || resolving || applyStarted || validationDisabled) return;
    const selected = new Set(rowNumbers);
    const dependent = field === 'workerId' ? Object.fromEntries(workflow.columns.filter((column) => column.key === 'entitlementId' || column.key === 'entryId').map((column) => [column.key, ''])) : {};
    const values = validatedRows.map((row) => selected.has(row.rowNumber) ? { ...row.values, [field]: option?.id ?? '', ...dependent } : row.values);
    setError('');
    setIdempotencyKey(crypto.randomUUID());
    void resolveRows(values, sourceRows.current);
  };

  const validate = async (mapped = false) => {
    if (resolving || applying || applyStarted || validationDisabled) return;
    setError('');
    try {
      const parsed = parseTSV(input, workflow.maxRows);
      if (columnMapping && !mapped) {
        setParsedInput(parsed);
        setMapping((current) => Object.fromEntries(workflow.columns.map((column) => [column.key, Object.hasOwn(current, column.key) && (!current[column.key] || parsed.headers.includes(current[column.key])) ? current[column.key] : parsed.headers.find((header) => header === column.key || header === column.label) ?? ''])));
        setStep('map'); return;
      }
      if (columnMapping) {
        const sources = Object.values(mapping).filter(Boolean);
        if (new Set(sources).size !== sources.length) throw new Error('같은 원본 열을 여러 항목에 연결할 수 없습니다.');
        parsed.rows = parsed.rows.map((row) => Object.fromEntries(workflow.columns.map((column) => [column.key, row[mapping[column.key]] ?? ''])));
        parsed.headers = workflow.columns.map((column) => column.key);
      } else if (referencePort) {
        const headers = parsed.headers.map((header) => workflow.columns.find((column) => column.label === header)?.key ?? header);
        if (new Set(headers).size !== headers.length) throw new Error('같은 항목에 해당하는 열이 중복되었습니다.');
        parsed.rows = parsed.rows.map((row) => Object.fromEntries(parsed.headers.map((header, index) => [headers[index], row[header]])));
        parsed.headers = headers;
      }
      const allowed = new Set(workflow.columns.map((column) => column.key));
      const unknown = parsed.headers.filter((header) => !allowed.has(header));
      const missing = workflow.columns.filter((column) => column.required && !parsed.headers.includes(column.key));
      if (unknown.length) throw new Error(`양식에 없는 열이 있습니다: ${unknown.join(', ')}`);
      if (missing.length) throw new Error(`필수 열이 없습니다: ${missing.map((column) => column.key).join(', ')}`);
      const values = preserveBulkReferenceSelections(parsed.rows, sourceRows.current, validatedRows);
      if (!await resolveRows(values, parsed.rows)) return;
      setApplyResults([]);
      setIdempotencyKey(crypto.randomUUID());
      setStep('review');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '붙여넣은 내용을 읽지 못했습니다.');
    }
  };

  const apply = async (targetRows = validatedRows) => {
    if (invalidRows.length || !targetRows.length || applyingRef.current || resolving || validationDisabled) return;
    applyingRef.current = true;
    setApplying(true);
    setApplyStarted(true);
    setError('');
    try {
      const result = await onApply(operationType, targetRows, idempotencyKey);
      if (!result) {
        onClose();
        return;
      }
      setApplyResults((current) => {
        const next = new Map(current.map((row) => [row.rowNumber, row]));
        result.rows.forEach((row) => next.set(row.rowNumber, row));
        return validatedRows.flatMap((row) => {
          const applied = next.get(row.rowNumber);
          return applied ? [applied] : [];
        });
      });
      setStep('result');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '대량 작업을 적용하지 못했습니다.');
      if (reason instanceof BulkPreApplyValidationError) {
        setApplyStarted(false);
        setValidatedRows((current) => current.map((row) => ({ ...row, errors: [...row.errors, ...(reason.rowErrors.get(row.rowNumber) ?? [])] })));
      }
    } finally {
      applyingRef.current = false;
      setApplying(false);
    }
  };

  return (
    <DialogSurface
      open={open}
      onClose={onClose}
      busy={applying || resolving}
      title={step === 'map' ? `${workflow.label} · 열 연결` : step === 'paste' ? `${workflow.label} · 붙여넣기` : step === 'review' ? `${workflow.label} · 적용 전 검증` : `${workflow.label} · 적용 결과`}
      help={<Stack spacing={1}>
        <Typography variant="body2">{workflow.description}</Typography>
        <Typography variant="body2">Excel 또는 Google Sheets에서 헤더를 포함한 범위를 복사해 붙여넣으세요.</Typography>
        {referencePort && <Typography variant="body2">구성원·조직 등 연결할 대상은 이름으로 입력하세요. 검토 화면에서 검색해 선택할 수도 있습니다.</Typography>}
        {(step === 'paste' || step === 'map') && help}
        {step !== 'map' && workflow.defaultsNotice && <Typography variant="body2">{workflow.defaultsNotice}</Typography>}
      </Stack>}
      size={step === 'paste' ? 'large' : 'wide'}
      mobilePresentation="fullscreen"
      contentMode={step === 'paste' || step === 'map' ? 'flow' : 'fill'}
      headerContent={<FlowSteps activeStep={step === 'paste' || step === 'map' ? 0 : step === 'review' ? 1 : 2} ariaLabel="대량 작업 단계" steps={['TSV 붙여넣기', '검증·미리보기', '일괄 적용']} />}
      actions={step === 'paste'
        ? <><Button disabled={resolving} onClick={onClose}>취소</Button><Button variant="contained" startIcon={<ContentPasteOutlined />} disabled={!input.trim() || resolving || validationDisabled} onClick={() => void validate()}>{resolving ? '대상 확인 중' : columnMapping ? '열 연결' : '검증'}</Button></>
        : step === 'map' ? <><Button disabled={resolving} onClick={() => setStep('paste')}>입력으로 돌아가기</Button><Button variant="fill" disabled={resolving || validationDisabled} onClick={() => void validate(true)}>검증·미리보기</Button></>
        : step === 'review'
          ? <><Button startIcon={<ArrowBackOutlined />} onClick={() => { setStep('paste'); setError(''); }} disabled={applying || resolving || applyStarted}>입력으로 돌아가기</Button><Box sx={{ flex: 1 }} /><Button onClick={onClose} disabled={applying || resolving}>{applyStarted ? '닫기' : '취소'}</Button><Button variant="contained" disabled={Boolean(invalidRows.length) || applying || resolving || validationDisabled || !validatedRows.length} onClick={() => void apply()}>{applying ? '적용 중' : resolving ? '대상 확인 중' : `${validatedRows.length.toLocaleString()}개 행 적용`}</Button></>
          : <><Box sx={{ flex: 1 }} /><Button onClick={onClose} disabled={applying}>닫기</Button>{failedResults.length ? <Button variant="contained" disabled={applying} onClick={() => void apply(validatedRows.filter((row) => resultByRow.get(row.rowNumber)?.status === 'FAILED'))}>{applying ? '재시도 중' : `실패 ${failedResults.length.toLocaleString()}개 행 재시도`}</Button> : null}</>}
    >
        {step === 'map' ? <Stack spacing={2}>
          <Typography variant="body2">명단의 열을 등록 항목에 연결해 주세요. 연결하지 않은 원본 열은 가져오지 않습니다.</Typography>
          {introduction}
          {error && <Alert severity="error">{error}</Alert>}
          {workflow.columns.map((column) => <FormTextField key={column.key} select disabled={resolving} label={column.label} required={column.required} value={mapping[column.key] ?? ''} onChange={(event) => { resolutionSequence.current += 1; setMapping((current) => ({ ...current, [column.key]: event.target.value })); }} fullWidth>
            <MenuItem value="">연결 안 함</MenuItem>{parsedInput?.headers.map((header) => <MenuItem key={header} value={header}>{header}</MenuItem>)}
          </FormTextField>)}
        </Stack> : step === 'paste' ? (
          <Stack spacing={2}>
            {introduction}
            {inputControls?.(resolving || applying || applyStarted)}
            {workflows.length > 1 && <FormTextField select disabled={resolving} label="작업 유형" value={operationType} onChange={(event) => changeWorkflow(event.target.value)}>{workflows.map((item) => <MenuItem key={item.operationType} value={item.operationType}><ValueBadge label={item.label} /></MenuItem>)}</FormTextField>}
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
                <Box><Typography variant="subtitle2">TSV 양식</Typography><Typography variant="caption" color="text.secondary">필수 열 · {workflow.columns.filter((column) => column.required).map((column) => columnMapping || referencePort ? column.label : column.key).join(', ')}</Typography></Box>
                {workflow.templateHref && <Button component="a" href={workflow.templateHref} download startIcon={<DownloadOutlined />}>Excel 양식 다운로드</Button>}
              </Stack>
            </Paper>
            <FormTextField
              label="TSV 붙여넣기"
              disabled={resolving}
              value={input}
              onChange={(event) => { resolutionSequence.current += 1; setInput(event.target.value); setError(''); }}
              multiline
              minRows={12}
              maxRows={20}
              placeholder={example}
              slotProps={{ htmlInput: { spellCheck: false, style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.6 } } }}
            />
            <Typography variant="caption" color="text.secondary">첫 행은 열 이름으로 사용합니다. 최대 {(workflow.maxRows ?? 10_000).toLocaleString()}개 행까지 한 번에 검증할 수 있습니다.</Typography>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
              <Box><Typography variant="h6">{step === 'result' ? `${validatedRows.length.toLocaleString()}개 행 적용 결과` : `${validatedRows.length.toLocaleString()}개 행이 만드는 결과`}</Typography><Typography variant="body2" color="text.secondary">각 행마다 다음 결과를 만듭니다: {workflow.resourceLabel}</Typography></Box>
              {step === 'review'
                ? <Stack direction="row" spacing={1}><StatusBadge tone="success" label={`적용 가능 ${validatedRows.length - invalidRows.length}`} /><StatusBadge tone={invalidRows.length ? 'warning' : 'neutral'} label={`오류 ${invalidRows.length}`} /></Stack>
                : <Stack direction="row" spacing={1}><StatusBadge tone="success" label={`성공 ${succeededResults.length}`} /><StatusBadge tone={failedResults.length ? 'warning' : 'neutral'} label={`실패 ${failedResults.length}`} /></Stack>}
            </Stack>
            {step === 'review' && invalidRows.length > 0 && <Alert severity="warning">오류가 있는 행을 수정해야 적용할 수 있습니다. 연결할 대상은 아래에서 검색하고, 그 밖의 값은 입력으로 돌아가 수정해 주세요.</Alert>}
            {step === 'review' && referencePort && <BulkReferenceReview workflow={workflow} rows={validatedRows} port={referencePort} disabled={applying || resolving || applyStarted} onChange={changeReference} />}
            {step === 'review' && applyStarted && error && <Alert severity="info">중복 처리를 막기 위해 적용을 시작한 내용은 수정할 수 없습니다. 같은 내용으로 다시 적용하거나 닫은 뒤 작업 목록에서 처리 결과를 확인해 주세요.</Alert>}
            {step === 'result' && failedResults.length > 0 && <Alert severity="warning">성공한 행은 이미 등록되었습니다. 실패한 행만 다시 시도할 수 있습니다.</Alert>}
            {step === 'result' && failedResults.length === 0 && <Alert severity="success">모든 행을 등록했습니다.</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table stickyHeader size="small" aria-label={step === 'result' ? '대량 작업 적용 결과' : '대량 작업 검증 결과'}>
                <TableHead><TableRow><TableCell sx={{ width: 64 }}>행</TableCell><TableCell sx={{ minWidth: 180 }}>{step === 'result' ? '처리 결과' : '검증'}</TableCell><TableCell sx={{ minWidth: 220 }}>생성 결과</TableCell>{workflow.previewColumns.map((column) => <TableCell key={column.label} sx={{ minWidth: 150 }}>{column.label}</TableCell>)}</TableRow></TableHead>
                <TableBody>{previewRows.map((row) => {
                  const result = resultByRow.get(row.rowNumber);
                  const failed = result?.status === 'FAILED';
                  return <TableRow key={row.rowNumber} sx={row.errors.length || failed ? { bgcolor: designTokens.color.warningSoft } : undefined}><TableCell>{row.rowNumber}</TableCell><TableCell>{step === 'result' ? <Stack spacing={0.25}><StatusBadge tone={failed ? 'warning' : 'success'} label={failed ? '실패' : '등록 완료'} />{result?.message ? <Typography variant="caption" color={failed ? 'error.main' : 'text.secondary'}>{result.message}</Typography> : null}</Stack> : row.errors.length ? <Stack spacing={0.25}><StatusBadge tone="warning" label="수정 필요" />{row.errors.map((message, index) => <Typography key={`${index}-${message}`} variant="caption" color="error.main">{message}</Typography>)}</Stack> : <StatusBadge tone="success" label="검증 통과" />}</TableCell><TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{workflow.resourceLabel}</Typography>{result?.resourceId ? <Typography variant="caption" color="text.secondary">{result.resourceId}</Typography> : null}</TableCell>{workflow.previewColumns.map((column) => <TableCell key={column.label}>{previewValue(column, row)}</TableCell>)}</TableRow>;
                })}</TableBody>
              </Table>
            </TableContainer>
            {validatedRows.length > previewRows.length && <Typography variant="caption" color="text.secondary">{step === 'result' ? '실패 행' : '오류 행'}을 우선으로 최대 200개 행까지 미리 보여줍니다.</Typography>}
          </Stack>
        )}
    </DialogSurface>
  );
}
